import React from 'react';
import { Image as ImageIcon } from 'lucide-react';

interface Props {
  enabled: boolean;
  position: 'header_left' | 'header_center' | 'header_right' | 'footer_left' | 'footer_center' | 'footer_right';
  logoUrl: string | null;
  label: string;
  onEnabledChange: (enabled: boolean) => void;
  onPositionChange: (position: 'header_left' | 'header_center' | 'header_right' | 'footer_left' | 'footer_center' | 'footer_right') => void;
}

const LogoPositionSelector: React.FC<Props> = ({
  enabled,
  position,
  logoUrl,
  label,
  onEnabledChange,
  onPositionChange
}) => {
  return (
    <div className="space-y-3">
      {/* Enable Toggle */}
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id={`logo-${label.toLowerCase().replace(/\s+/g, '-')}`}
          checked={enabled}
          onChange={(e) => onEnabledChange(e.target.checked)}
          className="rounded"
        />
        <label 
          htmlFor={`logo-${label.toLowerCase().replace(/\s+/g, '-')}`}
          className="font-medium text-text-primary dark:text-text-dark-primary cursor-pointer flex items-center gap-2"
        >
          <ImageIcon className="w-4 h-4" />
          {label}
        </label>
      </div>

      {/* Logo Preview */}
      {enabled && logoUrl && (
        <div className="ml-6 p-3 bg-background-tertiary dark:bg-background-dark-tertiary rounded-lg">
          <img 
            src={logoUrl} 
            alt={`${label} logo`}
            className="h-12 object-contain"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }}
          />
        </div>
      )}

      {/* Position Selector */}
      {enabled && (
        <div className="ml-6">
          <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
            Position
          </label>
          <select
            value={position}
            onChange={(e) => onPositionChange(e.target.value as typeof position)}
            className="input w-full"
          >
            <optgroup label="Header">
              <option value="header_left">Header - Left</option>
              <option value="header_center">Header - Center</option>
              <option value="header_right">Header - Right</option>
            </optgroup>
            <optgroup label="Footer">
              <option value="footer_left">Footer - Left</option>
              <option value="footer_center">Footer - Center</option>
              <option value="footer_right">Footer - Right</option>
            </optgroup>
          </select>
          
          {/* Visual Position Indicator */}
          <div className="mt-3 border border-border-light dark:border-border-dark rounded-lg overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-3 gap-2 p-2 bg-background-tertiary dark:bg-background-dark-tertiary border-b border-border-light dark:border-border-dark">
              <div className={`h-8 flex items-center justify-center ${position === 'header_left' ? 'bg-primary-500 rounded' : ''}`}>
                {position === 'header_left' && <ImageIcon className="w-4 h-4 text-white" />}
              </div>
              <div className={`h-8 flex items-center justify-center ${position === 'header_center' ? 'bg-primary-500 rounded' : ''}`}>
                {position === 'header_center' && <ImageIcon className="w-4 h-4 text-white" />}
              </div>
              <div className={`h-8 flex items-center justify-center ${position === 'header_right' ? 'bg-primary-500 rounded' : ''}`}>
                {position === 'header_right' && <ImageIcon className="w-4 h-4 text-white" />}
              </div>
            </div>
            
            {/* Content Area */}
            <div className="p-4 text-center text-xs text-text-tertiary dark:text-text-dark-tertiary">
              Content Area
            </div>
            
            {/* Footer */}
            <div className="grid grid-cols-3 gap-2 p-2 bg-background-tertiary dark:bg-background-dark-tertiary border-t border-border-light dark:border-border-dark">
              <div className={`h-8 flex items-center justify-center ${position === 'footer_left' ? 'bg-primary-500 rounded' : ''}`}>
                {position === 'footer_left' && <ImageIcon className="w-4 h-4 text-white" />}
              </div>
              <div className={`h-8 flex items-center justify-center ${position === 'footer_center' ? 'bg-primary-500 rounded' : ''}`}>
                {position === 'footer_center' && <ImageIcon className="w-4 h-4 text-white" />}
              </div>
              <div className={`h-8 flex items-center justify-center ${position === 'footer_right' ? 'bg-primary-500 rounded' : ''}`}>
                {position === 'footer_right' && <ImageIcon className="w-4 h-4 text-white" />}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No Logo Warning */}
      {enabled && !logoUrl && (
        <div className="ml-6 text-sm text-yellow-600 dark:text-yellow-400">
          ⚠️ No logo URL available. Please upload a logo in organization/workspace settings.
        </div>
      )}
    </div>
  );
};

export default LogoPositionSelector;

